import BlockContent from '@/components/BlockContent';
import CtaButton from '@/components/ui/buttons/CtaButton';
import React, { useState } from 'react';

type CollapsibleItemEntry = {
  _key: string;
  heading: string;
  content: Record<string, unknown>[];
  cta?: {
    title: string;
    route?: string;
    link?: string;
  };
};

interface CollapsibleItemsProps {
  title?: string;
  heading?: string;
  content?: Record<string, unknown>[];
  items: CollapsibleItemEntry[];
  bgColor?: { hex: string };
  bgGradient?: {
    gradientDirection: string;
    colorStart: { hex: string };
    colorStop: { hex: string };
  };
}

const CollapsibleItems: React.FC<CollapsibleItemsProps> = ({ title, heading, content, items, bgColor, bgGradient }) => {
  const [openIndex, setOpenIndex] = useState<number>(-1);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  const background =
    (bgColor && bgColor.hex) ||
    (bgGradient?.colorStart?.hex &&
      bgGradient?.colorStop?.hex &&
      `-webkit-linear-gradient(${bgGradient.gradientDirection}, ${bgGradient.colorStart.hex} 0%, ${bgGradient.colorStop.hex} 100%)`);

  return (
    <section className="w-full px-5 py-[60px]" style={background ? { background } : undefined}>
      <div className="mx-auto w-full" style={{ maxWidth: '860px' }}>
        {title && <p className="mb-2 text-sm uppercase tracking-[0.1em] opacity-70">{title}</p>}
        {heading && <h2 className="mb-6 text-[2rem]">{heading}</h2>}
        {content && (
          <div className="mb-8">
            <BlockContent content={content} />
          </div>
        )}
        <div className="w-full">
          {(items || []).map((item, index) => (
            <div key={item._key || index} className="border-b border-black/10 first:border-t">
              <button
                className="flex w-full items-center justify-between border-none bg-transparent py-5 text-left text-lg font-semibold text-inherit"
                onClick={() => toggleItem(index)}
                aria-expanded={openIndex === index}
              >
                <span>{item.heading}</span>
                <span className="ml-4 shrink-0 text-2xl font-light">{openIndex === index ? '−' : '+'}</span>
              </button>
              {openIndex === index && (
                <div className="pb-6">
                  {item.content && <BlockContent content={item.content} />}
                  {item.cta && (item.cta.route || item.cta.link) && (
                    <div className="mt-4">
                      <CtaButton to={item.cta.route || item.cta.link}>{item.cta.title}</CtaButton>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CollapsibleItems;
